import React, { useMemo, useState } from 'react';
import { format, parseISO, startOfMonth, endOfMonth, eachMonthOfInterval, setYear, startOfYear, endOfYear } from 'date-fns';
import { Card } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';
import type { Booking } from './BookingCalendar';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { BarChart3, Table as TableIcon } from 'lucide-react';
import { ToggleWithLabel } from '@/components/ui/toggle-with-label';

type MonthlyRevenueProps = {
  bookings: Booking[];
};

type MonthlyData = {
  month: string;
  shortMonth: string;
  revenue: number;
  bookingCount: number;
  paidRevenue: number;
  bookedRevenue: number;
  pendingRevenue: number;
};

export function MonthlyRevenue({ bookings }: MonthlyRevenueProps) {
  const [showChart, setShowChart] = useState(false);

  const monthlyData = useMemo(() => {
    const year2025 = setYear(new Date(), 2025);
    const yearStart = startOfYear(year2025);
    const yearEnd = endOfYear(year2025);
    
    const months = eachMonthOfInterval({
      start: yearStart,
      end: yearEnd
    });

    const monthlyRevenue = months.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);
      
      const monthBookings = bookings.filter(booking => {
        try {
          // Extract date part to avoid issues with ISO strings
          const bookingDateClean = booking.date.includes('T') 
            ? booking.date.split('T')[0] 
            : booking.date;
            
          const bookingDate = parseISO(bookingDateClean);
          return bookingDate >= monthStart && bookingDate <= monthEnd;
        } catch (error) {
          console.error("Error filtering bookings by month:", error);
          return false;
        }
      });

      const paidRevenue = monthBookings
        .filter(b => b.status === 'paid')
        .reduce((sum, b) => sum + (b.revenue?.amount || 0), 0);

      const bookedRevenue = monthBookings
        .filter(b => b.status === 'booked')
        .reduce((sum, b) => sum + (b.revenue?.amount || 0), 0);

      const pendingRevenue = monthBookings
        .filter(b => b.status === 'pending')
        .reduce((sum, b) => sum + (b.revenue?.amount || 0), 0);

      return {
        month: format(month, 'MMMM yyyy'),
        shortMonth: format(month, 'MMM'),
        revenue: paidRevenue + bookedRevenue + pendingRevenue,
        bookingCount: monthBookings.length,
        paidRevenue,
        bookedRevenue,
        pendingRevenue
      };
    });

    return monthlyRevenue;
  }, [bookings]);

  const totalRevenue = useMemo(() => 
    monthlyData.reduce((sum, month) => sum + month.revenue, 0)
  , [monthlyData]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = monthlyData.find(m => m.shortMonth === label);
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border">
          <p className="font-semibold text-gray-900">{data?.month}</p>
          <div className="space-y-1 mt-2">
            <p className="text-blue-600">
              Paid: {formatCurrency(payload[0].value)}
            </p>
            <p className="text-emerald-600">
              Booked: {formatCurrency(payload[1].value)}
            </p>
            <p className="text-amber-600">
              Pending: {formatCurrency(payload[2].value)}
            </p>
            <p className="text-gray-600 text-sm mt-2">
              Total Bookings: {data?.bookingCount}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Monthly Revenue Overview 2025</h3>
            <ToggleWithLabel
              id="chart-toggle"
              checked={showChart}
              onChange={setShowChart}
              label="Chart View"
              leftIcon={<TableIcon className="h-4 w-4" />}
              rightIcon={<BarChart3 className="h-4 w-4" />}
            />
          </div>

          {showChart ? (
            <div className="h-[400px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthlyData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                  barGap={0}
                  barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  {/* Fix: Add explicit default props instead of relying on defaultProps */}
                  <XAxis
                    dataKey="shortMonth"
                    tick={{ fill: '#6B7280' }}
                    tickLine={{ stroke: '#E5E7EB' }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    scale="band"
                    padding={{ left: 0, right: 0 }}
                    allowDecimals={false}
                    allowDataOverflow={false}
                    allowDuplicatedCategory={false}
                    hide={false}
                    interval="preserveEnd"
                    minTickGap={0}
                    orientation="bottom"
                    tickCount={5}
                    tickMargin={0}
                    reversed={false}
                  />
                  {/* Fix: Add explicit default props instead of relying on defaultProps */}
                  <YAxis
                    tickFormatter={formatCurrency}
                    tick={{ fill: '#6B7280' }}
                    tickLine={{ stroke: '#E5E7EB' }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    scale="linear"
                    padding={{ top: 0, bottom: 0 }}
                    allowDecimals={true}
                    allowDataOverflow={false}
                    hide={false}
                    orientation="left"
                    tickCount={5}
                    tickMargin={0}
                    minTickGap={5}
                    interval="preserveEnd"
                    reversed={false}
                    domain={[0, 'auto']}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    name="Paid Revenue"
                    dataKey="paidRevenue"
                    fill="#3B82F6"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    name="Booked Revenue"
                    dataKey="bookedRevenue"
                    fill="#10B981"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    name="Pending Revenue"
                    dataKey="pendingRevenue"
                    fill="#F59E0B"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Month</th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">Paid</th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">Booked</th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">Pending</th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">Total</th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">Bookings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {monthlyData.map((month, index) => (
                    <tr key={month.month}>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {month.month}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-blue-600 font-medium">
                        {formatCurrency(month.paidRevenue)}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-emerald-600 font-medium">
                        {formatCurrency(month.bookedRevenue)}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-amber-600 font-medium">
                        {formatCurrency(month.pendingRevenue)}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-900 font-medium">
                        {formatCurrency(month.revenue)}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-600">
                        {month.bookingCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      Total
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-blue-600">
                      {formatCurrency(monthlyData.reduce((sum, m) => sum + m.paidRevenue, 0))}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-emerald-600">
                      {formatCurrency(monthlyData.reduce((sum, m) => sum + m.bookedRevenue, 0))}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-amber-600">
                      {formatCurrency(monthlyData.reduce((sum, m) => sum + m.pendingRevenue, 0))}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(totalRevenue)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {monthlyData.reduce((sum, month) => sum + month.bookingCount, 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}